import { useEffect, useMemo, useState, type ReactNode } from "react";
import "./index.css";
import { searchTools } from "./lib/fuzzy";
import { formatUpdateDate, recentlyUpdated, type RecentTool } from "./lib/recent";
import { TOOL_STATUS_LABELS, type Tool, type ToolsResponse } from "./lib/types";

const SUGGEST_TOOL_EMAIL = "askOS.tech@au.dk";
const SUGGEST_TOOL_SUBJECT = "Tool suggestion for trit.tools";
const SUGGEST_TOOL_BODY = `Hi TRIT team,

I'd like to suggest a tool for trit.tools.

What should the tool do?
-

Who would use it?
-

Does it handle personal data / does it need to be GDPR safe?
-

Similar or existing tools (links, if any):
-

Anything else we should know?
-
`;

const SUGGEST_TOOL_URL = `mailto:${SUGGEST_TOOL_EMAIL}?subject=${encodeURIComponent(
  SUGGEST_TOOL_SUBJECT,
)}&body=${encodeURIComponent(SUGGEST_TOOL_BODY)}`;

const ACRONYMS = new Set(["llm", "llms", "ai", "api", "sql", "css", "json"]);

/** "no-code" → "No Code", "llms" → "LLMs". */
function displayCategory(category: string): string {
  return category
    .split(/[-\s]+/)
    .map(word =>
      ACRONYMS.has(word)
        ? word.toUpperCase().replace(/S$/, "s")
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

type Theme = "light" | "dark";

function useTheme() {
  const [theme, setTheme] = useState<Theme | null>(
    () => (localStorage.getItem("theme") as Theme | null) ?? null,
  );

  useEffect(() => {
    if (theme) {
      document.documentElement.dataset.theme = theme;
      localStorage.setItem("theme", theme);
    }
  }, [theme]);

  const toggle = () => {
    const current: Theme =
      theme ?? (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    setTheme(current === "dark" ? "light" : "dark");
  };

  return toggle;
}

/** Shared dialog shell: overlay, close button, Escape-to-close. */
function Modal({
  labelledBy,
  onClose,
  children,
}: {
  labelledBy: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={event => event.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // e.g. permission denied — fall through to the textarea fallback.
  }
  // Fallback for non-secure (http) contexts and older browsers.
  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.append(area);
  area.select();
  const ok = document.execCommand("copy");
  area.remove();
  return ok;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="copy-button"
      onClick={async () => {
        if (await copyText(text)) setCopied(true);
      }}
    >
      {copied ? "Copied ✓" : label}
    </button>
  );
}

function SuggestToolModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal labelledBy="suggest-title" onClose={onClose}>
      <h2 id="suggest-title">Suggest a Tool</h2>
      <p className="modal-description">
        Email your idea to the TRIT team. If you have a mail app set up, the button below opens a
        prefilled draft — otherwise copy the address and template into your webmail (Outlook,
        Gmail, …).
      </p>

      <div className="suggest-row">
        <code className="suggest-email">{SUGGEST_TOOL_EMAIL}</code>
        <CopyButton text={SUGGEST_TOOL_EMAIL} label="Copy address" />
      </div>

      <div className="suggest-template-header">
        <h3>Template</h3>
        <CopyButton text={SUGGEST_TOOL_BODY} label="Copy template" />
      </div>
      <pre className="suggest-template">{SUGGEST_TOOL_BODY}</pre>

      <a className="modal-open-link" href={SUGGEST_TOOL_URL}>
        Open in your email app
      </a>
    </Modal>
  );
}

function ToolLink({ tool, className, children }: { tool: Tool; className?: string; children: ReactNode }) {
  const external = /^https?:\/\//.test(tool.url);
  // Site-relative tool URLs ("/foo") live under the same base path as the
  // app, which may be a subpath — so link them relative to the page instead
  // of the domain root.
  const href = external ? tool.url : tool.url.replace(/^\//, "");
  return (
    <a
      className={className}
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function StatusBadge({ tool }: { tool: Tool }) {
  if (!tool.status) return null;
  return (
    <span className={`status-badge status-${tool.status}`}>
      {TOOL_STATUS_LABELS[tool.status]}
    </span>
  );
}

function ToolCard({ tool, onShowInfo }: { tool: Tool; onShowInfo: (tool: Tool) => void }) {
  return (
    <article className="tool-card">
      {/* Stretched link: covers the whole card via CSS, keeps HTML valid. */}
      <ToolLink tool={tool} className="tool-card-link">
        <h2>{tool.name}</h2>
      </ToolLink>
      <p>{tool.description}</p>
      <div className="tool-card-footer">
        <StatusBadge tool={tool} />
        <button
          className="info-button"
          onClick={() => onShowInfo(tool)}
          aria-label={`Full info for ${tool.name}`}
          title="Full info & changelog"
        >
          ⓘ
        </button>
      </div>
    </article>
  );
}

function RecentlyUpdated({
  recent,
  onShowInfo,
}: {
  recent: RecentTool[];
  onShowInfo: (tool: Tool) => void;
}) {
  return (
    <section className="recent" aria-label="Recently updated">
      <h2 className="section-heading">Recently updated</h2>
      <ul className="recent-list">
        {recent.map(({ tool, date, notes }) => (
          <li key={tool.id}>
            <button
              className="recent-item"
              onClick={() => onShowInfo(tool)}
              aria-label={`Full info for ${tool.name}`}
            >
              <span className="recent-name">{tool.name}</span>
              <span className="recent-date">Updated {formatUpdateDate(date)}</span>
              {notes && <span className="recent-notes">{notes}</span>}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ToolInfoModal({ tool, onClose }: { tool: Tool; onClose: () => void }) {
  return (
    <Modal labelledBy="modal-title" onClose={onClose}>
      <h2 id="modal-title">{tool.name}</h2>
        <div className="modal-meta">
          <StatusBadge tool={tool} />
          {tool.categories.map(category => (
            <span key={category} className="chip chip-small">
              {displayCategory(category)}
            </span>
          ))}
        </div>

        <p className="modal-description">{tool.description}</p>
        {tool.details && <p className="modal-details">{tool.details}</p>}

        {tool.changelog && (
          <section className="changelog" aria-label="Changelog">
            <h3>Changelog</h3>
            <ol>
              {tool.changelog.map((entry, index) => (
                <li key={`${entry.version}-${index}`}>
                  <div className="changelog-entry-header">
                    <span className="changelog-version">{entry.version}</span>
                    {entry.date && (
                      <span className="changelog-date">{formatUpdateDate(entry.date)}</span>
                    )}
                  </div>
                  {entry.notes && <p className="changelog-notes">{entry.notes}</p>}
                </li>
              ))}
            </ol>
          </section>
        )}

        <ToolLink tool={tool} className="modal-open-link">
          Open tool {/^https?:\/\//.test(tool.url) ? "↗" : "→"}
        </ToolLink>
    </Modal>
  );
}

export function App() {
  const [data, setData] = useState<ToolsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [infoTool, setInfoTool] = useState<Tool | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const toggleTheme = useTheme();

  useEffect(() => {
    // Relative (no leading slash) so it resolves against the base path the
    // app is served from, which may be a subpath behind a reverse proxy.
    fetch("api/tools")
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<ToolsResponse>;
      })
      .then(setData)
      .catch(err => setError(String(err)));
  }, []);

  const visible = useMemo(() => {
    if (!data) return [];
    const pool = category
      ? data.tools.filter(tool => tool.categories.includes(category))
      : data.tools;
    return searchTools(pool, query);
  }, [data, query, category]);

  const recent = useMemo(() => (data ? recentlyUpdated(data.tools) : []), [data]);
  const showRecent = query.trim() === "" && category === null && recent.length > 0;

  if (error) {
    return (
      <div className="app">
        <p className="status">Failed to load tools: {error}</p>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="top-bar">
        <button className="outline-button" onClick={() => setSuggestOpen(true)}>
          Suggest a Tool
        </button>
        <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle light/dark mode">
          ☀
        </button>
      </div>

      <header className="header">
        <h1>TRIT Tools Directory</h1>
        <p className="tagline">Discover tools developed by trit to help you in your projects</p>
        <input
          className="search"
          type="search"
          placeholder="Search tools…"
          value={query}
          onChange={event => setQuery(event.target.value)}
          autoFocus
        />
      </header>

      {data && (
        <nav className="tabs" aria-label="Categories">
          <button
            className={`tab ${category === null ? "tab-active" : ""}`}
            onClick={() => setCategory(null)}
          >
            All Tools
          </button>
          {data.categories.map(cat => (
            <button
              key={cat}
              className={`tab ${category === cat ? "tab-active" : ""}`}
              onClick={() => setCategory(current => (current === cat ? null : cat))}
            >
              {displayCategory(cat)}
            </button>
          ))}
        </nav>
      )}

      {!data && !error && <p className="status">Loading tools…</p>}

      {showRecent && <RecentlyUpdated recent={recent} onShowInfo={setInfoTool} />}

      {data && visible.length === 0 && <p className="status">No tools found</p>}

      <main className="grid">
        {visible.map(tool => (
          <ToolCard key={tool.id} tool={tool} onShowInfo={setInfoTool} />
        ))}
      </main>

      {infoTool && <ToolInfoModal tool={infoTool} onClose={() => setInfoTool(null)} />}

      {suggestOpen && <SuggestToolModal onClose={() => setSuggestOpen(false)} />}
    </div>
  );
}

export default App;
